import { useState, useRef, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { useAppContext } from '../../../../app/provider/useAppContext';
import { appConfig } from '../../../../config/app-config';
import { useMiniBasketData } from "../../hooks/useMiniBasketDataHook";
import { getCart } from "../../utils/cartUtil";

import { useMiniBasketActions } from "./hooks/useMiniBasketActions";
import MiniBasketDropdown from "./miniBasketDropdown";
import MiniBasketMobileView from "./miniBasketMobileView";
import { BasketProps } from "./miniBasketTypes";

/**
 * Mini-Basket Component
 * Renders a dropdown (desktop) or bottom sheet (mobile) to show cart items.
 * Refactored into specialized hooks and sub-components.
 */
const Basket = ({ children, items: propItems, isOpen, eventKey }: BasketProps) => {
    const [open, setOpen] = useState(false);
    const { items: hookItems, cart: hookCart } = useMiniBasketData();
    const items = propItems || hookItems;
    const containerRef = useRef<HTMLDivElement>(null);
    const navigate = useNavigate();
    const params = useParams();
    const pathParts = window.location.pathname.split('/').filter(Boolean);
    const storeKey = params.storeKey || (pathParts.length > 0 ? pathParts[0] : appConfig.defaults.storeKey);
    const resolvedEventKey = eventKey || params.eventKey || (pathParts.length > 1 ? pathParts[1] : appConfig.defaults.eventKey);
    const [context] = useAppContext();

    const basketActions = useMiniBasketActions(items, hookCart);

    // Helper function to format price
    const formatPrice = (amount: number) => {
        const currency = context?.currency || sessionStorage.getItem('currency') || 'USD';
        const locale = context?.locale || sessionStorage.getItem('locale') || 'en';
        return new Intl.NumberFormat(locale, {
            style: 'currency',
            currency: currency,
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(amount);
    };

    // Checkout handler
    const handleCheckout = () => {
        setOpen(false);
        const pingToken = sessionStorage.getItem('ping_access_token');
        const isLoggedIn = context?.loggedIn || !!pingToken;

        if (!isLoggedIn) {
            const redirectPath = `/${storeKey}/checkout`;
            localStorage.setItem('postLoginRedirect', redirectPath);
            navigate(`/${storeKey}/${resolvedEventKey}/account?returnUrl=${encodeURIComponent(redirectPath)}`);
        } else {
            navigate(`/${storeKey}/checkout`);
        }
    };

    const handleViewFullBasket = () => {
        setOpen(false);
        navigate(`/${storeKey}/cart`);
    };

    // Listen for custom event to open Basket
    useEffect(() => {
        const handleOpenBasket = () => setOpen(true);
        window.addEventListener('openBasket', handleOpenBasket);
        return () => window.removeEventListener('openBasket', handleOpenBasket);
    }, []);

    // Close on click outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !(containerRef.current as HTMLElement).contains(event.target as Node)) {
                setOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // Auto-close if basket becomes empty (e.g. user deletes last item)
    useEffect(() => {
        if (open && items && items.length === 0) {
            setOpen(false);
        }
    }, [items, open]);

    return (
        <div className="relative" ref={containerRef}>
            {/* Basket Button / Trigger */}
            <div
                onClick={async () => {
                    const cart = await getCart();
                    if (!cart || !cart.lineItems || cart.lineItems.length === 0) {
                        navigate(`/${storeKey}/cart`);
                    } else {
                        setOpen(!open);
                    }
                }}
                className="cursor-pointer"
            >
                {children}
            </div>

            {/* Arrow caret – desktop/tablet only, centered below the icon */}
            {(open || isOpen) && (
                <div
                    className="hidden md:block absolute z-[51] pointer-events-none"
                    style={{
                        top: '100%',
                        left: '50%',
                        /*
                         * marginTop: 4 puts the unrotated element top at 4px below
                         * the container bottom. Its centre = 4+8 = 12px = dropdown top
                         * (mt-3). Upper bounding-box half (~11px) sits in the gap;
                         * lower half embeds into Section A white — creating the caret.
                         */
                        marginTop: 4,
                        transform: 'translateX(-50%) rotate(-45deg)',
                        width: 16,
                        height: 16,
                        background: 'white',
                        borderRight: '1px solid #e5e7eb',
                        borderTop: '1px solid #e5e7eb',
                        borderRadius: 3,
                    }}
                />
            )}

            {/* Dropdown - Desktop Only */}
            <MiniBasketDropdown
                open={open}
                isOpen={isOpen}
                items={items}
                groups={basketActions.groups}
                looseItems={basketActions.looseItems}
                groupedCount={basketActions.groupedCount}
                updatingItems={basketActions.updatingItems}
                eventKey={eventKey}
                onClose={() => setOpen(false)}
                handleDelete={basketActions.handleDelete}
                handleIncrement={basketActions.handleIncrement}
                handleDecrement={basketActions.handleDecrement}
                handleGroupDelete={basketActions.handleGroupDelete}
                handleGroupIncrement={basketActions.handleGroupIncrement}
                handleGroupDecrement={basketActions.handleGroupDecrement}
            />

            {/* Mobile View - Bottom Sheet */}
            <MiniBasketMobileView
                open={open}
                isOpen={isOpen}
                subtotal={basketActions.subtotal}
                formatPrice={formatPrice}
                handleCheckout={handleCheckout}
                handleViewFullBasket={handleViewFullBasket}
            />
        </div>
    );
};

export default Basket;
